<template>
    <div class="relative">
        <div ref="trigger" class="cursor-pointer" @click="toggle">
            <slot :is-open="isOpen" name="trigger" />
        </div>

        <CTransitionSlideDown>
            <div
                v-if="isOpen"
                ref="dropdownContent"
                class="absolute z-50 mt-2 overflow-hidden rounded-xl bg-white dark:bg-surface-card shadow-lg ring-1 ring-black/5 dark:ring-white/10"
                :class="[
                    placement === 'right' ? 'right-0' : 'left-0',
                    paddingNone ? '' : 'p-3',
                    filterWrapper ? 'w-full bg-gray-100 dark:bg-subtle' : 'min-w-[280px] max-w-[calc(100vw-2rem)] sm:min-w-[320px]',
                    filter
                        ? 'left-0 right-0 w-auto'
                        : mobileBottomSheet
                          ? 'max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:mt-0 max-sm:w-auto max-sm:max-w-none max-sm:rounded-b-none'
                          : 'max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:w-auto'
                ]"
            >
                <CClose v-if="!filter && !filterWrapper" class="sm:hidden" @close="close()" />
                <slot :close="close" name="content" />
            </div>
        </CTransitionSlideDown>
    </div>
</template>

<script setup lang="ts">
import CClose from '@/components/ui/CClose.vue'
import CTransitionSlideDown from '@/components/ui/transitions/CTransitionSlideDown.vue'
import { ref, watch, onMounted, onUpdated, onUnmounted } from 'vue'

const _CDropdownPlacementTypes = ['left', 'right'] as const
type CDropdownPlacementType = (typeof _CDropdownPlacementTypes)[number]

interface Props {
    placement?: CDropdownPlacementType
    filter?: boolean
    filterWrapper?: boolean
    disabled?: boolean
    paddingNone?: boolean
    mobileBottomSheet?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    placement: 'left',
    filter: false,
    filterWrapper: false,
    disabled: false,
    paddingNone: false,
    mobileBottomSheet: false
})

const emit = defineEmits<{
    (e: 'open'): void
    (e: 'close'): void
}>()

const isOpen = ref(false)
const trigger = ref<HTMLElement | null>(null)
const dropdownContent = ref<HTMLElement | null>(null)

const TRIGGER_CONTROL_SELECTOR = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/** The focusable element inside the trigger slot (a button or an input), not the wrapper div */
function getTriggerControl() {
    return trigger.value?.querySelector<HTMLElement>(TRIGGER_CONTROL_SELECTOR) ?? null
}

/**
 * The trigger is slot content, so screen readers would never learn the open state from the wrapper.
 * Put it on the control itself. Runs after every render, so a re-rendered control gets it back.
 */
function syncTriggerAria() {
    const control = getTriggerControl()
    if (!control) return

    control.setAttribute('aria-haspopup', 'true')
    control.setAttribute('aria-expanded', String(isOpen.value))
}

function handleKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !isOpen.value) return

    close()
    getTriggerControl()?.focus()
}

// Listen only while open: many dropdowns live on one page
watch(isOpen, (value) => {
    if (value) {
        document.addEventListener('keydown', handleKeydown)
    } else {
        document.removeEventListener('keydown', handleKeydown)
    }
})

const handleClickOutside = (event: MouseEvent) => {
    if (!isOpen.value) return

    const target = event.target as HTMLElement
    const triggerEl = trigger.value
    const contentEl = dropdownContent.value

    if (!triggerEl?.contains(target) && !contentEl?.contains(target)) {
        close()
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    syncTriggerAria()
})

onUpdated(syncTriggerAria)

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeydown)
})

const toggle = () => {
    if (props.disabled) return

    isOpen.value = !isOpen.value

    if (isOpen.value) {
        emit('open')
    }
}

const open = () => {
    isOpen.value = true
    emit('open')
}

const close = () => {
    if (!props.filter || isOpen.value) {
        isOpen.value = false
        emit('close')
    }
}

defineExpose({
    open,
    close
})
</script>
